import type { DetectionTrendRow, DeviceActivityRow, SubnetGroup } from '../../lib/api'

/**
 * Turning the read models into the shapes charts want.
 *
 * The dashboard's endpoints return one row per device per day and one per rule per day, because that
 * is what the projections store and what the tables used to print. Every chart on the page is some
 * fold of those two lists — by day, by device, by rule, by severity — so the folds live here as
 * ordinary functions rather than inside components. That keeps the widgets to layout, and it means
 * the arithmetic can be tested without rendering anything, which matters because an off-by-one in a
 * day axis produces a chart that is wrong rather than a chart that is broken.
 */

/** Every day present in the data, oldest first. Days with no rows simply do not appear. */
export function days(rows: readonly { day: string }[]): string[] {
  return [...new Set(rows.map((row) => row.day))].sort()
}

/** One value per day, aligned to `days`, with a nought for a day the rows do not mention. */
export function sumByDay<TRow extends { day: string }>(
  rows: readonly TRow[],
  axis: readonly string[],
  value: (row: TRow) => number
): number[] {
  const totals = new Map<string, number>()
  for (const row of rows) totals.set(row.day, (totals.get(row.day) ?? 0) + value(row))
  return axis.map((day) => totals.get(day) ?? 0)
}

export interface Ranked {
  key: string
  value: number
}

/**
 * The biggest few of something, summed across the window.
 *
 * Summed rather than taking the largest single day: a device that was busy every day matters more
 * than one that was busy once, and the daily view is the other chart on the same screen.
 */
export function topBy<TRow>(
  rows: readonly TRow[],
  key: (row: TRow) => string,
  value: (row: TRow) => number,
  limit = 10
): Ranked[] {
  const totals = new Map<string, number>()
  for (const row of rows) totals.set(key(row), (totals.get(key(row)) ?? 0) + value(row))

  return [...totals.entries()]
    .map(([entryKey, entryValue]) => ({ key: entryKey, value: entryValue }))
    .sort((left, right) => right.value - left.value || left.key.localeCompare(right.key))
    .slice(0, limit)
}

const SEVERITY_NAMES: Record<number, string> = {
  0: 'Unknown',
  1: 'Informational',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Critical',
  6: 'Fatal'
}

/**
 * How the window's events divide by the worst severity each row reached.
 *
 * Ordered most serious first, so the slice somebody is looking for is the one at the top of the
 * legend rather than wherever the arithmetic happened to put it.
 */
export function severityMix(rows: readonly { highestSeverity: number; eventCount: number }[]): Ranked[] {
  const totals = new Map<number, number>()
  for (const row of rows) totals.set(row.highestSeverity, (totals.get(row.highestSeverity) ?? 0) + row.eventCount)

  return [...totals.entries()]
    .filter(([, value]) => value > 0)
    .sort(([left], [right]) => right - left)
    .map(([severity, value]) => ({
      key: SEVERITY_NAMES[severity] ?? `Severity ${severity}`,
      value
    }))
}

/** The daily event counts for one device, aligned to the shared day axis, for a sparkline. */
export function trendFor(
  rows: readonly DeviceActivityRow[],
  deviceName: string,
  axis: readonly string[]
): number[] {
  return sumByDay(
    rows.filter((row) => row.deviceName === deviceName),
    axis,
    (row) => row.eventCount
  )
}

export interface Coverage {
  subnets: string[]
  covered: number[]
  uncovered: number[]
}

/**
 * Policy coverage per subnet, as two stacked parts.
 *
 * The uncovered count is drawn against the baseline rather than on top, because it is the number the
 * view exists to surface: a subnet where nothing is deployed is either a gap or a decision, and only
 * the segment touching the axis can be compared across columns by eye.
 *
 * Sorted by how many machines are uncovered, so the worst subnet is first and a fleet with fifty
 * subnets still puts the problem on the screen without anybody scrolling for it.
 */
export function coverage(subnets: readonly SubnetGroup[], limit = 12): Coverage {
  const ranked = [...subnets]
    .sort((left, right) => right.devicesWithNoPolicy - left.devicesWithNoPolicy || right.deviceCount - left.deviceCount)
    .slice(0, limit)

  return {
    subnets: ranked.map((subnet) => subnet.subnet),
    uncovered: ranked.map((subnet) => subnet.devicesWithNoPolicy),
    covered: ranked.map((subnet) => Math.max(0, subnet.deviceCount - subnet.devicesWithNoPolicy))
  }
}

/** The rules that fired most, by matches, across the window. */
export const noisiestRules = (rows: readonly DetectionTrendRow[], limit = 10): Ranked[] =>
  topBy(rows, (row) => row.ruleName, (row) => row.matchCount, limit)

/** The devices that produced the most events across the window. */
export const busiestDevices = (rows: readonly DeviceActivityRow[], limit = 10): Ranked[] =>
  topBy(rows, (row) => row.deviceName, (row) => row.eventCount, limit)
